import getNewCreditsEarned from "@faire/web-api/api/brand-referrals/retailer/retailerToken/non-viewed-referral-reward-count/get";
import { IBrandReferralPromotion } from "@faire/web-api/indigofair/brand/referrals/IBrandReferralPromotion";
import { IGetNonViewedReferralRewardCountResponse } from "@faire/web-api/indigofair/brand/referrals/IGetNonViewedReferralRewardCountResponse";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { fulfilledOrStale } from "@faire/web/common/instanceUtils";
import { makeObservable } from "@faire/web/common/makeObservable";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { action, computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";

import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getReferralPromotions } from "@faire/retailer/serialized-data/getReferralPromotions";
import { getSettingR2BAwardConfiguration } from "@faire/retailer/settings/getSettingR2BAwardConfiguration";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

/**
 * @deprecated prefer to use `useR2BAwardStore` or `withStores` instead
 */
export class R2BAwardStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(R2BAwardStore);

  @observable
  private newCreditsNotViewedResponse?: IPromiseBasedObservable<IGetNonViewedReferralRewardCountResponse>;

  constructor() {
    makeObservable(this);
    this.initialize();
  }

  private initialize = () => {
    if (isLoggedInRetailer()) {
      this.fetchNewCreditsNotViewed();
    }
  };

  @action
  fetchNewCreditsNotViewed = () => {
    this.newCreditsNotViewedResponse = fromPromise(
      getNewCreditsEarned(RetailerStore.get().retailerToken!),
      this.newCreditsNotViewedResponse
    );
  };

  @computed
  get newCreditsNotViewed(): number | undefined {
    return fulfilledOrStale(
      this.newCreditsNotViewedResponse,
      "non_viewed_referral_reward_count"
    )?.non_viewed_referral_reward_count;
  }

  get incentiveActivationAmount(): IFaireMoney {
    return moneyOrZero(getSettingR2BAwardConfiguration().r2b_activation_amount);
  }

  get incentiveActivationAmountFormatted(): string {
    return this.formatAward(this.incentiveActivationAmount);
  }

  private get incentivePerOrderAmount(): IFaireMoney {
    return moneyOrZero(getSettingR2BAwardConfiguration().r2b_per_order_amount);
  }

  get incentivePerOrderAmountFormatted(): string {
    return this.formatAward(this.incentivePerOrderAmount);
  }

  get activePromotions(): Set<IBrandReferralPromotion.PromotionType> {
    return new Set(
      getReferralPromotions()
        ?.map((brandReferralPromotion) => brandReferralPromotion.promotion_type)
        .filter(
          (
            promotionType
          ): promotionType is IBrandReferralPromotion.PromotionType =>
            promotionType !== undefined
        )
    );
  }

  // The R2B reward that we should advertise to retailers in most places.
  @computed
  get r2bAdvertisedRetailerAward(): IFaireMoney {
    return this.r2bIncentiveMaxAward;
  }

  // The R2B reward that we should advertise to retailers in most places, formatted as a string.
  @computed
  get r2bAdvertisedRetailerAwardFormatted(): string {
    return this.formatAward(this.r2bAdvertisedRetailerAward);
  }

  private formatAward = (award: IFaireMoney) => {
    return formatMoney({
      money: award,
      omitCentsIfZero: true,
      disableAutoConvertCurrency: true,
    });
  };

  private get r2bIncentiveMaxAward(): IFaireMoney {
    return moneyOrZero(getSettingR2BAwardConfiguration().r2b_max_credits);
  }

  get hasActivationAmount(): boolean {
    if (this.incentiveActivationAmount.amount_cents) {
      return this.incentiveActivationAmount.amount_cents > 0;
    }
    return false;
  }
}

export const useR2BAwardStore = createStoreHook(R2BAwardStore);
